import { AnyAction } from 'redux';
import { dateFormat } from 'design-system';
import { format } from 'date-fns';

// Data Layer
import {
  validLegalEntity,
  customerWithoutNulls,
  appliesAutoPxDiscount,
} from 'data-layer-cr/dist/functions/quotations';
import { QuotationData } from 'data-layer-cr/dist/responseObjects';
import { NetworkResponse } from 'data-layer-cr/dist/network/NetworkTypes';
import {
  NATURAL_PERSON,
  LEGAL_PERSON,
} from 'data-layer-cr/dist/constants/customerTypes';
import { matchCurrentDate } from 'data-layer-cr/dist/functions/helpers';
import { QuotationUnit, ServerResponse } from 'data-layer-cr/dist/models';
import { setRequestStatus } from 'data-layer-cr/dist/network';

import { setUserValues } from 'src/analytics';

import {
  findAutoPxIndex,
  findAutoPxUnit,
} from 'src/functions/quotationMethods';

import { AsyncAppThunk, AsyncDispatch, AppThunk } from '../../ThunkTypes';
import {
  requestQuotationSuccess,
  setSelectedPackageOption,
  setEmissionLog,
  setAutoPxFlag,
  setHasLicense,
  setHasSerialMotor,
  updateQuotationStatus,
  setLocalSelectedPackageOption,
} from '../slice/quotationSlice';
import {
  updateDataFields,
  updatePaymentQuantity,
} from '../../emissionPolicy/slice/emissionPolicySlice';

// Models
import defaultDataFieldValues from '../../../constants/defaultDataFieldValues';
import { makeEmptyEmissionDataFields } from '../../../models/helperTypes/EmissionDataFields';

// Services
import processRequest from '../../../services/processRequest';
import { REQUEST_QUOTATION } from '../requestIdentifiers';
/**
 * Requests the quotation related to the provided hash.
 * It will then save in the store the value returned for both the quotation and
 * its related emission log (null if no log is registered).
 *
 * @param hashQuotation - The hash that uniquely identifies the quotation
 */
export const getQuotation =
  (hashQuotation: string): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(REQUEST_QUOTATION, 'requesting'));
    try {
      const response = await processRequest<NetworkResponse<QuotationData>>({
        method: 'GET',
        url: `quotation?hash=${hashQuotation}`,
      });
      const { quotation, emissionLog } = response.data;
      const { customerType, expireAt, quotationProfile } = quotation;

      setUserValues(
        quotation.user.name,
        quotation.user.email,
        quotation.brokerLicense,
      );

      quotation.units.sort((unitA, unitB) =>
        unitA.package.order > unitB.package.order ? 1 : -1,
      );

      /** Do not process expired quotations */
      if (matchCurrentDate(expireAt)) {
        throw new Error();
      }

      /** guard against possible null values */
      const customer = customerWithoutNulls(quotation);
      const legalEntity = validLegalEntity(quotation);

      /**
       * The only place where we could possibly get the customer information without
       * performing another request is here, so this action dispatches it.
       */
      if (customerType === NATURAL_PERSON) {
        dispatch(
          updateDataFields({
            ...makeEmptyEmissionDataFields(),
            ...defaultDataFieldValues,
            names: customer.firstName,
            lastNames: customer.lastName,
            email: customer.email,
            phoneNumber: customer.phone,
            birthday: dateFormat.dateFromServerString(customer.birthday),
            gender: customer.gender,
            effectiveDate: dateFormat.dateFromServerString(
              format(new Date(), 'dd/MM/yyyy'),
            ),
          }),
        );
      } else {
        dispatch(
          updateDataFields({
            ...makeEmptyEmissionDataFields(),
            ...defaultDataFieldValues,
            idType: LEGAL_PERSON,
            legalEntityName: legalEntity.name,
            email: legalEntity.email,
            phoneNumber: legalEntity.phone,
          }),
        );
      }

      let maxAllowedPayments = 12;

      /** Temporary solution: when is autopx, calls endpoint
       * to add a discount to units
       */

      if (appliesAutoPxDiscount(quotation)) {
        maxAllowedPayments = 12;

        const autoPxUnits = await processRequest<
          ServerResponse<QuotationUnit[]>
        >({
          method: 'POST',
          url: `Quotation/priceperunit?profileId=${quotationProfile.id}`,
          data: quotation.units,
        });

        quotation.units.forEach((unit, index) => {
          const responseAutoPxTotal = findAutoPxUnit(
            autoPxUnits.data[index].total.discounts,
          );

          if (unit.total.discounts && responseAutoPxTotal) {
            maxAllowedPayments = responseAutoPxTotal.maxQuotas;
            const autoPxIndex = findAutoPxIndex(unit.total.discounts);
            // eslint-disable-next-line no-param-reassign
            unit.total.discounts[autoPxIndex].total = responseAutoPxTotal.total;
          }
        });
      }

      /**
       * Here we will set the default value for the payment quantities based on the product, since
       * different product types vary in their max allowed value and we don't want an overflow.
       */
      dispatch(updatePaymentQuantity(maxAllowedPayments));

      dispatch(setHasLicense(Boolean(quotation.units[0].unit.licensePlate)));
      dispatch(setHasSerialMotor(Boolean(quotation.units[0].unit.serialMotor)));
      dispatch(setEmissionLog(emissionLog));
      dispatch(requestQuotationSuccess(quotation));
      return dispatch(setRequestStatus(REQUEST_QUOTATION, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(REQUEST_QUOTATION, 'failure'));
    }
  };

/**
 * Requests the quotation related to the provided hash.
 * It will then save in the store the value returned for both the quotation
 *
 * @param hashQuotation - The hash that uniquely identifies the quotation
 */
export const getCurrentQuotation =
  (hashQuotation: string, packageOption: number): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(REQUEST_QUOTATION, 'requesting'));
    try {
      const response = await processRequest<NetworkResponse<QuotationData>>({
        method: 'GET',
        url: `quotation?hash=${hashQuotation}`,
      });
      const { quotation } = response.data;
      const { expireAt, quotationProfile } = quotation;

      quotation.units.sort((unitA, unitB) =>
        unitA.package.order > unitB.package.order ? 1 : -1,
      );

      quotation.packageOption = packageOption;

      /** Do not process expired quotations */
      if (matchCurrentDate(expireAt)) {
        throw new Error();
      }

      let maxAllowedPayments = 12;

      /** Temporary solution: when is autopx, calls endpoint
       * to add a discount to units
       */

      if (appliesAutoPxDiscount(quotation)) {
        maxAllowedPayments = 12;

        const autoPxUnits = await processRequest<
          ServerResponse<QuotationUnit[]>
        >({
          method: 'POST',
          url: `Quotation/priceperunit?profileId=${quotationProfile.id}`,
          data: quotation.units,
        });

        quotation.units.forEach((unit, index) => {
          const responseAutoPxTotal = findAutoPxUnit(
            autoPxUnits.data[index].total.discounts,
          );

          if (unit.total.discounts && responseAutoPxTotal) {
            maxAllowedPayments = responseAutoPxTotal.maxQuotas;
            const autoPxIndex = findAutoPxIndex(unit.total.discounts);
            // eslint-disable-next-line no-param-reassign
            unit.total.discounts[autoPxIndex].total = responseAutoPxTotal.total;
          }
        });
      }

      /**
       * Here we will set the default value for the payment quantities based on the product, since
       * different product types vary in their max allowed value and we don't want an overflow.
       */
      dispatch(updatePaymentQuantity(maxAllowedPayments));

      dispatch(requestQuotationSuccess(quotation));
      return dispatch(setRequestStatus(REQUEST_QUOTATION, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(REQUEST_QUOTATION, 'failure'));
    }
  };

/**
 * Used when the user selects a custom package (when the quotation allows it)
 *
 * @param packageOption - The newly selected package
 */
export const setPackageOption =
  (packageOption: number): AppThunk =>
  (dispatch): void => {
    dispatch(setSelectedPackageOption(packageOption));
  };

export const setLocalPackageOption =
  (packageOption: number): AppThunk =>
  (dispatch): void => {
    dispatch(setLocalSelectedPackageOption(packageOption));
  };

export const setAutoPx =
  (isAutoPx: boolean): AppThunk =>
  (dispatch): void => {
    dispatch(setAutoPxFlag(isAutoPx));
  };

export const setQuotationStatus =
  (status: number): AppThunk =>
  (dispatch): void => {
    dispatch(updateQuotationStatus(status));
  };
